import { z } from 'zod';
import { COMMON_CONSENT_CODES } from '../commonConsents/commonConsents';

const PRODUCT_TYPES = ['GENERAL', 'PRESALE', 'EVENT', 'SET', 'CONCERT'] as const;
const PRODUCT_STATUSES = ['ON_SALE', 'SALE_ENDED', 'HIDDEN', 'PREPARING'] as const;

export const PRODUCT_LOCALES = ['ko-KR', 'en-US', 'ja-JP', 'zh-CN'] as const;
export type ProductLocale = (typeof PRODUCT_LOCALES)[number];
/** QA-456 상품 혜택 제외(고도몰 [혜택 제외]) — 회원 추가 할인 / 회원 중복 할인 / 회원 추가 마일리지 / 상품 쿠폰. */
export const BENEFIT_EXCLUSIONS = ['GRADE_DISCOUNT', 'GRADE_OVERLAP_DISCOUNT', 'GRADE_MILEAGE', 'COUPON'] as const;
export type BenefitExclusion = (typeof BENEFIT_EXCLUSIONS)[number];

export const productTypeSchema = z.enum(PRODUCT_TYPES);
export const productStatusSchema = z.enum(PRODUCT_STATUSES);

const uuid = z.string().uuid();

// 카테고리 id 는 BE 가 VARCHAR(36) String 으로 받고, 시드 하위 카테고리 id 는 UUID 형식이
// 아닌 값(예: '00000000-…-ck04', 'ck' 비-hex)도 존재한다. uuid() 강제 시 하위 카테고리 선택만으로
// "Invalid uuid" 검증 실패 → 저장 차단되므로, 비어있지 않은 문자열(≤64)로만 검증한다.
const categoryId = z.string().min(1).max(64);

const moneySchema = z.object({
  amount: z.number().min(0),
  currencyCode: z.string().min(3).max(3),
});

const currencyPriceSchema = z.object({
  listAmount: z.number().min(0),
  saleAmount: z.number().min(0),
});

// 외화(USD/JPY/CNY)는 선택적 오버라이드. 비우면(null) BE 가 환율로 자동계산하고,
// 입력하면 고정값으로 전송한다. listAmount/saleAmount 는 각각 독립적으로 비울 수 있다.
const overrideCurrencyPriceSchema = z.object({
  listAmount: z.number().min(0).nullable(),
  saleAmount: z.number().min(0).nullable(),
});

export const pricesSchema = z.object({
  KRW: currencyPriceSchema,
  USD: overrideCurrencyPriceSchema,
  JPY: overrideCurrencyPriceSchema,
  CNY: overrideCurrencyPriceSchema,
});

export type CurrencyPrices = z.infer<typeof pricesSchema>;
export type CurrencyPriceOverride = z.infer<typeof overrideCurrencyPriceSchema>;

const variantSchema = z.object({
  // ID42: 옵션명 다국어 wire(translations.{locale}.options[].optionId)는 BE 가 부여한 옵션ID 가
  // 필요하므로 편집 시에만 채워진다. detail 의 variantId 를 그대로 사용(신규 옵션=null, 번역 미지원).
  variantId: z.string().nullable().optional(),
  variantCode: z.string().max(50).nullable().optional(),
  // QA-521 — 옵션이 1개(옵션 없는 상품)면 비워도 된다(저장 시 "기본"). 2개 이상이면 아래 superRefine 이 필수로 막는다.
  label: z.string().max(100),
  additionalAmount: z.number().min(0).nullable().optional(),
  initialStockQty: z.number().int().min(0).nullable().optional(),
  // 안전재고 임계(저재고 알림 기준). per-variant. products variant safety_stock_qty.
  safetyStockQty: z.number().int().min(0).nullable().optional(),
  // TASK-2 재고추적 사용 여부 — **per-variant**(BE variant.stock_tracking_enabled, VariantCreateDto).
  // true(기본)=재고 차감·품절 관리, false=무한판매(재고 미설정, 차감 안 함). 편집 시 null=미변경.
  // OFF면 그 옵션의 initialStockQty/safetyStockQty 입력 무의미. §2-2·§6-1(K-1 해석 C).
  stockTrackingEnabled: z.boolean().nullable().optional(),
  // 결정① — 옵션 실측 무게(그램 정수, V108). null=미설정(상품 무게 폴백). 해외배송비 실무게 산정.
  // BE 편집 sync: null=미변경, 0=미설정 clear, 양수=설정 — clear 는 mapper 가 baseline 인지로 주입.
  weightGrams: z.number().int().min(0).nullable().optional(),
  // 자체 옵션코드(SKU 식별자, be-catalog V126 option_code — 고도몰 "자체 옵션 코드"). 최대 64자, 중복 검사 없음.
  // 편집 sync: null/미전송=미변경, ""=지움, 값=설정 — 지움은 mapper 가 baseline 인지로 주입한다.
  optionCode: z.string().max(64).nullable().optional(),
});

// #10c 분리형 조합 옵션 — be-catalog optionGroups 계약. create body 에 optionGroups 가 있으면
// 평면 variants 를 무시하고 그룹 간 데카르트 곱으로 조합 variant 를 BE 가 생성한다(상한 100).
// 결정④ — 수정(update)도 reconcile 지원: groupId/choiceId 는 GET option-groups 응답에서 시드되는
// 선택적 매칭 키. 모든 선택지가 id 를 가지면 ID-우선 매칭(rename-safe — 이름/라벨이 바뀌어도 기존
// variant 재고/SKU 생존), id 없는 행은 이름/라벨 매칭 폴백. wire 의 choice id 필드명은 groupChoiceId
// (mapper 가 변환). ★id 는 save 후 재생성되므로 편집 재진입 시 GET 재시드(캐시 무효화) 필수.
const optionChoiceSchema = z.object({
  choiceId: z.string().max(36).nullable().optional(),
  label: z.string().min(1, 'products.errors.optionChoiceLabelRequired').max(100),
  additionalAmount: z.number().min(0).nullable().optional(),
  position: z.number().int().min(0).optional(),
});

const optionGroupSchema = z.object({
  groupId: z.string().max(36).nullable().optional(),
  name: z.string().min(1, 'products.errors.optionGroupNameRequired').max(100),
  required: z.boolean().optional(),
  position: z.number().int().min(0).optional(),
  choices: z.array(optionChoiceSchema).min(1, 'products.errors.optionGroupChoicesRequired'),
});

export type OptionChoiceInput = z.infer<typeof optionChoiceSchema>;
export type OptionGroupInput = z.infer<typeof optionGroupSchema>;

// 예약판매(프리오더) 상태. null = 일반 판매. (BE PresaleStatus enum)
const PRESALE_STATUSES = ['PRESALE_SCHEDULED', 'PRESALE_OPEN'] as const;
export const presaleStatusSchema = z.enum(PRESALE_STATUSES);
export type PresaleStatus = (typeof PRESALE_STATUSES)[number];

const SHIPPING_FEE_TYPES = ['FREE', 'FLAT', 'CONDITIONAL', 'REGION'] as const;
export const shippingFeeTypeSchema = z.enum(SHIPPING_FEE_TYPES);
export type ShippingFeeType = (typeof SHIPPING_FEE_TYPES)[number];

// ID42 옵션명 번역 — translations.{locale}.options[]{optionId, name, choices[{choiceId,label}]}.
// BO 상품폼은 옵션 1계층(flat variant)이라 choices 는 비워둔다(BE 가 옵션=variant 일 때 무시).
const optionTranslationSchema = z.object({
  optionId: z.string(),
  name: z.string().max(100).nullable().optional(),
  choices: z
    .array(
      z.object({
        choiceId: z.string(),
        label: z.string().max(100).nullable().optional(),
      }),
    )
    .optional(),
});

// ID42 #14: variant(옵션/재고 SKU) 라벨 로케일별 번역. 상관키=variantCode(폼이 이미 보유).
// PDP 옵션 셀렉터(PdpOptionSheet)가 렌더하는 사용자 노출 라벨. null/생략 = ko 정본 fallback.
const variantLabelTranslationSchema = z.object({
  variantCode: z.string(),
  label: z.string().max(100).nullable().optional(),
});

const translationSchema = z.object({
  name: z.string().max(255).nullable().optional(),
  description: z.string().nullable().optional(),
  brandName: z.string().max(150).nullable().optional(),
  eventPromoSubtitle: z.string().max(120).nullable().optional(),
  // ID42 전필드 다국어 — product_translation 확장(detail_html/notice_text/policy_text/
  // shipping_policy_text + localized notice_spec). BE TranslationDto 와 1:1.
  detailHtml: z.string().nullable().optional(),
  noticeText: z.string().nullable().optional(),
  policyText: z.string().nullable().optional(),
  shippingPolicyText: z.string().nullable().optional(),
  noticeSpec: z.record(z.string()).nullable().optional(),
  // 옵션명(product_options) 번역 — BO 옵션관리 UI 부재로 보류(omit). 키=optionId. (방어적 round-trip)
  options: z.array(optionTranslationSchema).optional(),
  // ID42 #14: variant 라벨 번역(variantCode 상관). 사용자 노출 옵션 라벨 다국어.
  variants: z.array(variantLabelTranslationSchema).optional(),
});

const translationsSchema = z
  .object({
    'ko-KR': translationSchema.optional(),
    'en-US': translationSchema.optional(),
    'ja-JP': translationSchema.optional(),
    'zh-CN': translationSchema.optional(),
  })
  .optional();

export type ProductTranslations = z.infer<typeof translationsSchema>;

const yearMonthRegex = /^[0-9]{4}-(0[1-9]|1[0-2])$/;
const countryRegex = /^[A-Z]{2}$/;

// `<input type="datetime-local">` 은 'YYYY-MM-DDTHH:mm' (초·타임존 없음) 을, 비울 때는 '' 을 낸다.
// z.string().datetime() 은 초 + 'Z'/offset 이 필수라 두 값 모두 거부 → "판매 기간" 으로 포커스가
// 튀고 저장(CU)이 막혔다. 빈 값 / datetime-local / 풀 ISO 를 모두 허용하고, 실제 Instant 변환은
// detailMapper(toInstant) 에서 수행한다.
const optionalLocalDateTime = z
  .string()
  .nullable()
  .optional()
  .refine((v) => v == null || v === '' || !Number.isNaN(new Date(v).getTime()), {
    message: 'products.errors.dateTimeFormat',
  });

// `<input type="date">` ('YYYY-MM-DD') + 빈 값 허용. 값이 있으면 형식 검증.
const optionalDate = z
  .string()
  .nullable()
  .optional()
  .refine((v) => v == null || v === '' || /^\d{4}-\d{2}-\d{2}$/.test(v), {
    message: 'products.errors.dateFormat',
  });

/**
 * 검색 키워드 1개 최대 글자 수 — catalog ProductCreate/UpdateRequest `List<@Size(max = 255) String>` 와 같게(catalog PR #38).
 * 종전 50자였을 때 상품명이 그대로 키워드로 들어간 고도몰 이관 상품(약 4,447개, 최장 113자)은 본문만 고쳐도 저장이
 * 안 됐다(QA-193). 255 = 상품명 한도.
 */
export const SEARCH_KEYWORD_MAX = 255;

export const productFormSchema = z
  .object({
    // 1. 기본
    productType: productTypeSchema,
    // 상품상태(판매 축): ON_SALE/SALE_ENDED/HIDDEN/PREPARING. A-2 에서 노출여부(displayed)와 분리.
    status: productStatusSchema,
    // 수동 품절(be-cat V130, 고도몰 품절 표시) — 재고와 무관하게 판매를 막는다. 상세 응답에 필드가 있을 때만
    // 값이 있다(등록 폼·필드 없는 응답은 undefined → 체크박스 숨김). 저장은 바뀌었을 때만 보낸다(detailMapper).
    manualSoldOut: z.boolean().optional(),
    // 노출여부(전시 on/off). BE `displayed` (BUG-010 옵션 B). 상품상태와 독립적인 축.
    displayed: z.boolean().optional(),
    // F-1 B2B 전용 상품. BE `b2bOnly`(top-level, products.b2b_only, default false).
    // true=B2B(사업자) 리스트에서만 노출, false(기본)=전체 노출. displayed 와 독립 축.
    b2bOnly: z.boolean().optional(),
    // 구매·접근 권한(be-cat V131·V132, 고도몰 goodsPermission/goodsAccess). ALL = 회원+비회원, MEMBER = 회원전용,
    //  GRADE = 특정회원등급(purchaseGradeIds / accessGradeIds — user-service grade_id, 1개 이상 필수).
    //  purchaseDeniedText — 구매 불가 고객(비회원)에게 가격 대신 보일 문구(최대 100자, 빈 값 = 지움).
    //  accessDeniedDisplay — 접근 회원전용일 때 비회원 목록에도 노출할지(false = 숨김).
    purchasePermission: z.enum(['ALL', 'MEMBER', 'GRADE']).optional(),
    purchaseGradeIds: z.array(z.string().max(36)).optional(),
    purchaseDeniedText: z.string().trim().max(100, 'products.errors.purchaseDeniedTextTooLong').optional(),
    // 고도몰 "가격 대체문구 사용" 체크(폼 전용). BE 는 문구 유무 하나로 저장 — 끄면 "" 전송(문구 지움).
    purchaseDeniedTextEnabled: z.boolean().optional(),
    accessPermission: z.enum(['ALL', 'MEMBER', 'GRADE']).optional(),
    accessGradeIds: z.array(z.string().max(36)).optional(),
    accessDeniedDisplay: z.boolean().optional(),
    // QA-456 혜택 제외(고도몰 [혜택 제외], be-cat commerceMeta.benefitExclusions·benefitExclusionGradeIds).
    //  benefitExclusions — 제외할 혜택. 상세 응답에 필드가 없으면(BE 반영 전) undefined → 저장 때 보내지 않는다(기존 값 유지).
    //  benefitExclusionTarget — 폼 전용. ALL = 전체회원(gradeIds = []), GRADE = 고른 등급만.
    benefitExclusions: z.array(z.enum(BENEFIT_EXCLUSIONS)).optional(),
    benefitExclusionTarget: z.enum(['ALL', 'GRADE']).optional(),
    benefitExclusionGradeIds: z.array(z.string().max(36)).optional(),
    // QA-456 2단계 마일리지 지급 대상(고도몰 [마일리지 설정 → 지급 대상], be-cat commerceMeta.mileageGradeIds).
    //  폼 전용 mileageTarget: ALL = 전체회원([]), GRADE = 고른 등급, NONE = 지급 안 함(["NONE"]).
    //  상세에 필드가 없으면(BE 반영 전) undefined → 저장 때 보내지 않는다(기존 값 유지).
    mileageTarget: z.enum(['ALL', 'GRADE', 'NONE']).optional(),
    mileageGradeIds: z.array(z.string().max(36)).optional(),
    // QA-579 마일리지 사용 제외(결제 단계에서 보유 마일리지를 이 상품에 못 쓰게) — be-cat commerceMeta.mileageUseExclusionGradeIds.
    //  폼 전용 mileageUseExclusionTarget: NONE = 제외 안 함([]), ALL = 전체 회원(["ALL"]), GRADE = 고른 등급만.
    //  상세 응답에 필드가 없으면(BE 반영 전) undefined → 저장 때 보내지 않는다.
    mileageUseExclusionTarget: z.enum(['NONE', 'ALL', 'GRADE']).optional(),
    mileageUseExclusionGradeIds: z.array(z.string().max(36)).optional(),
    // ID39 노출 예약 — display window. start→노출 시작, end→종료. end 빈값(null)=상시노출.
    // BE display_start_at/display_end_at(Instant nullable). 스케줄러가 window 따라 displayed 토글.
    displayStartAt: optionalLocalDateTime,
    displayEndAt: optionalLocalDateTime,
    // mall-visibility 파트1 — 상품 노출 국가(몰) 집합. 폼은 "노출 대상 몰" 코드 배열을 보유한다.
    // 기본 = 전체노출(4몰 전부). detailMapper 가 be-cat 와이어 규칙(전체노출 표현)으로 변환한다.
    visibleMalls: z.array(z.enum(PRODUCT_LOCALES)).optional(),
    // QA-507 공통 동의 4종 사용 여부(문구는 [동의 문구 관리]). 고른 코드만 — 빈 배열 = 모두 사용 안 함.
    commonConsentCodes: z.array(z.enum(COMMON_CONSENT_CODES)).optional(),
    primaryCategoryId: categoryId.nullable(),
    additionalCategoryIds: z.array(categoryId).max(10).optional(),
    // A-2: brandId 는 GET /catalog/brands 의 brandId(BE brand_id VARCHAR(36)). 현재 시드는 UUID 지만,
    // 카테고리 id 전례(시드 비-UUID 로 uuid() 강제가 저장 차단)처럼 strict uuid() 는 회귀 위험이 있어
    // 비어있지 않은 문자열(≤36)로만 검증한다(BE 길이 제약과 정합).
    brandId: z.string().min(1).max(36).nullable().optional(),
    artistName: z.string().max(150).nullable().optional(),
    artistId: uuid.nullable().optional(),
    artistMemberIds: z
      .array(uuid)
      .max(30, 'products.errors.artistMembersTooMany')
      .nullable()
      .optional(),
    nameKo: z
      .string()
      .min(1, 'products.errors.nameKoRequired')
      .max(255, 'products.errors.nameKoTooLong'),
    nameEn: z.string().max(255).nullable().optional(),
    // QA-231 — 상품코드(product_code)는 서버 자동배정 고유값으로 전환(복제 시 -COPY 접미사 폐지,
    // 모든 상품 고유 자동 배정). 클라이언트 필수 입력이 아니며 폼에서는 read-only 로 표시한다.
    // 레거시 라운드트립을 위해 표시용 값은 유지하되 검증은 선택(optional)으로 완화한다.
    productCode: z
      .string()
      .trim()
      .max(50, 'products.errors.productCodeTooLong')
      .nullable()
      .optional(),
    // QA-231 자체상품코드(self_product_code) — 앨범별 고정 코드(상품코드와 별개 목적). 선택 입력,
    // 최대 50자. 복제/등록 시 그대로 유지된다(고정값). 비우면 미설정.
    selfProductCode: z
      .string()
      .trim()
      .max(50, 'products.errors.selfProductCodeTooLong')
      .nullable()
      .optional(),
    barcode: z.string().max(50).nullable().optional(),
    // 옵션명(catalog V135) — 옵션 선택칸 위 이름(예 "MEMBER"). 수정 화면에서만 PATCH 로 저장(useProductOptionNameMutation).
    optionName: z.string().max(255).nullable().optional(),
    originCountry: z
      .string()
      .regex(countryRegex, 'products.errors.originCountryFormat')
      .nullable()
      .optional(),
    manufacturerName: z.string().max(150).nullable().optional(),
    manufacturedYearMonth: z
      .string()
      .regex(yearMonthRegex, 'products.errors.yearMonthFormat')
      .nullable()
      .optional(),

    // 2. 다국어 (storefront PDP 노출용 — 백오피스 i18n과 별개)
    translations: translationsSchema,
    eventPromoSubtitle: z.string().max(120).nullable().optional(),

    // 3. 가격
    listPrice: moneySchema,
    salePrice: moneySchema,
    prices: pricesSchema.optional(),

    // 4. 일정
    salesStartAt: optionalLocalDateTime,
    salesEndAt: optionalLocalDateTime,
    releaseDate: optionalDate,
    stockInboundDate: optionalDate,
    shippingStartAt: optionalLocalDateTime,
    purchaseLimitMin: z.number().int().min(1).nullable().optional(),
    purchaseLimitMax: z.number().int().min(1).nullable().optional(),
    // QA-542 구매수량 기준(고도몰 fixedOrderCnt) — OPTION(옵션별, 기본)·PRODUCT(상품 합산)·MEMBER(아이디 누적).
    purchaseLimitBasis: z.enum(['OPTION', 'PRODUCT', 'MEMBER']).optional(),

    // 5. 옵션 — Task#4: 옵션은 선택사항(이커머스 표준). 행이 0개여도 유효 — 등록 시 variants 를
    // 생략하면 BE 가 기본 옵션 1개를 자동 생성한다(#10a createVariants). 행이 있으면 라벨은 필수.
    variants: z.array(variantSchema),
    // #10c 분리형 조합 옵션(선택). 비어있으면 평면 variants 경로. 1개 이상이면 BE 가 그룹
    // 데카르트 곱으로 조합 variant 생성(평면 variants 무시). 조합 상한 100(아래 superRefine).
    optionGroups: z.array(optionGroupSchema).max(20).optional(),

    // QA-R6 G(옵션 A) — 등록 시점 조합별 재고. 조합은 저장 시 서버가 데카르트 곱으로 생성하므로
    // 등록 페이로드엔 조합 행이 없다. 운영자가 등록 화면에서 입력한 조합별 초기/안전 재고·무게를
    // 조합 키(comboKeyOf, 그룹순 라벨 튜플)로 보관했다가, create 매퍼가 데카르트 곱 순서대로 페이로드에
    // 싣는다. EDIT 경로는 SkuTable 조합 행(서버 생성 variant 기반)이 담당하므로 이 필드는 등록 전용.
    comboStocks: z
      .record(
        z.object({
          initialStockQty: z.number().int().min(0).nullable().optional(),
          safetyStockQty: z.number().int().min(0).nullable().optional(),
          weightGrams: z.number().min(0).nullable().optional(),
          stockTrackingEnabled: z.boolean().nullable().optional(),
          // 등록 시 조합별 자체 옵션코드(comboStocks[].optionCode).
          optionCode: z.string().max(64).nullable().optional(),
        }),
      )
      .optional(),

    // 6. 상세 본문 & 이미지
    thumbnailUrl: z.string().max(500).nullable().optional(),
    detailImages: z.array(z.string()).max(50).optional(),
    detailHtml: z.string().nullable().optional(),

    // 7. 배송 (배송비 책정)
    shippingFree: z.boolean().optional(),
    wishlistEnabled: z.boolean().optional(),
    shippingFeeType: shippingFeeTypeSchema.nullable().optional(),
    baseFee: z.number().min(0).nullable().optional(),
    islandExtraFee: z.number().min(0).nullable().optional(),
    freeThreshold: z.number().min(0).nullable().optional(),
    returnFee: z.number().min(0).nullable().optional(),
    exchangeFee: z.number().min(0).nullable().optional(),
    // M-8 ID81 배송비 마스터(fee-policies) 참조. be-catalog product DTO
    // shippingFeePolicyId(VARCHAR36 — order shipping_fee_policy.policyId). null =
    // 미지정(기본 템플릿 자동 적용). §8 정책연결의 shippingPolicyId(Long, 배송 안내
    // 텍스트정책)와 다른 필드임에 주의.
    shippingFeePolicyId: z.string().max(36).nullable().optional(),
    // ① Task#7 — 상품 실측 무게(그램 정수, be-catalog V107 weightGrams). 해외배송비 실무게 산정.
    // null = 미설정(전송 시 omit → BE 미변경 보존; BE PUT 0 = clear, 양수 = 설정).
    weightGrams: z.number().int().min(0).nullable().optional(),

    // 8. 정책 연결 (배송안내/안내사항/운영방침 → 정책관리 select)
    shippingPolicyId: z.number().int().positive().nullable().optional(),
    guidePolicyId: z.number().int().positive().nullable().optional(),
    operationPolicyId: z.number().int().positive().nullable().optional(),

    // 9. 상품 정보 고시
    noticeSpec: z.record(z.string()).optional(),

    // 10. R5 A-2 이커머스 보강 (commerceMeta — 요청은 flat, 응답은 commerceMeta{})
    taxExempt: z.boolean().optional(), // false = 과세(10%)
    searchKeywords: z
      .array(z.string().max(SEARCH_KEYWORD_MAX, 'products.errors.searchKeywordTooLong'))
      .max(30)
      .nullable()
      .optional(),
    sortWeight: z.number().int().nullable().optional(), // 클수록 우선 노출
    seoTitle: z.string().max(255).nullable().optional(),
    seoDescription: z.string().max(500).nullable().optional(),
    relatedProductIds: z.array(z.string()).max(20).nullable().optional(),
    presaleStatus: presaleStatusSchema.nullable().optional(), // null = 일반 판매
  })
  .superRefine((v, ctx) => {
    // QA-521 옵션 라벨 — 행이 2개 이상일 때만 필수(1개면 저장 시 기본 라벨).
    if (v.variants.length < 2) return;
    v.variants.forEach((variant, i) => {
      if (!variant.label.trim()) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'products.errors.variantLabelRequired', path: ['variants', i, 'label'] });
      }
    });
  })
  .refine(
    (v) =>
      v.purchaseLimitMin == null ||
      v.purchaseLimitMax == null ||
      v.purchaseLimitMin <= v.purchaseLimitMax,
    { message: 'products.errors.purchaseLimitOrder', path: ['purchaseLimitMax'] },
  )
  .refine(
    (v) => {
      // ID39 노출 예약: 종료 < 시작 차단(UX). BE replaceDisplayWindow 도 end<start 거부하나
      // 클라에서 먼저 막아 잘못된 window 저장 시도를 피한다. 한쪽 비움/미설정은 통과(상시노출/단일경계).
      const start = v.displayStartAt;
      const end = v.displayEndAt;
      if (!start || !end) return true;
      const s = new Date(start).getTime();
      const e = new Date(end).getTime();
      if (Number.isNaN(s) || Number.isNaN(e)) return true; // 파싱불가는 datetime refine 가 따로 거부
      return e >= s;
    },
    { message: 'products.errors.displayWindowOrder', path: ['displayEndAt'] },
  )
  .refine(
    (v) => {
      // A-1 정책: 판매가 ≤ 정가 (할인 허용 → 정가≠판매가 OK, 가격 인상만 차단).
      // 실제 편집되는 값은 prices.KRW (가격 UI 가 prices.* 를 바인딩). 레거시는 listPrice/salePrice 폴백.
      const krw = v.prices?.KRW;
      const list = krw ? krw.listAmount : v.listPrice.amount;
      const sale = krw ? krw.saleAmount : v.salePrice.amount;
      return sale <= list;
    },
    { message: 'products.errors.salePriceTooHigh', path: ['prices', 'KRW', 'saleAmount'] },
  )
  .refine(
    (v) => v.artistId != null || !v.artistMemberIds || v.artistMemberIds.length === 0,
    {
      message: 'products.errors.artistMemberIdsRequireArtist',
      path: ['artistMemberIds'],
    },
  )
  .refine(
    // V132 — 특정회원등급이면 등급을 1개 이상 골라야 한다(BE 는 비면 400).
    (v) => v.purchasePermission !== 'GRADE' || (v.purchaseGradeIds?.length ?? 0) > 0,
    { message: 'products.errors.purchaseGradeRequired', path: ['purchaseGradeIds'] },
  )
  .refine(
    (v) => v.accessPermission !== 'GRADE' || (v.accessGradeIds?.length ?? 0) > 0,
    { message: 'products.errors.accessGradeRequired', path: ['accessGradeIds'] },
  )
  .refine(
    // QA-456 — 혜택 제외를 특정회원등급으로 두면 등급을 1개 이상(혜택을 고르지 않았으면 대상은 상관없다).
    (v) =>
      (v.benefitExclusions?.length ?? 0) === 0 || v.benefitExclusionTarget !== 'GRADE' || (v.benefitExclusionGradeIds?.length ?? 0) > 0,
    { message: 'products.errors.benefitExclusionGradeRequired', path: ['benefitExclusionGradeIds'] },
  )
  .refine(
    (v) => v.mileageUseExclusionTarget !== 'GRADE' || (v.mileageUseExclusionGradeIds?.length ?? 0) > 0,
    { message: 'products.errors.mileageUseExclusionGradeRequired', path: ['mileageUseExclusionGradeIds'] },
  )
  .refine(
    (v) => v.mileageTarget !== 'GRADE' || (v.mileageGradeIds?.length ?? 0) > 0,
    { message: 'products.errors.mileageGradeRequired', path: ['mileageGradeIds'] },
  )
  .refine(
    // mall-visibility — 노출 몰을 명시했다면 최소 1개는 선택해야 한다(전 몰 미노출 = 고아 상품 차단).
    // undefined(미설정) = 전체노출 기본이라 통과.
    (v) => v.visibleMalls === undefined || v.visibleMalls.length > 0,
    { message: 'products.errors.visibleMallsRequired', path: ['visibleMalls'] },
  )
  .refine(
    (v) => {
      // #10c 조합 상한 100 — 그룹별 선택지 수의 곱(데카르트 곱)이 100 을 넘으면 거부(BE 상한 미러).
      const groups = v.optionGroups;
      if (!groups || groups.length === 0) return true;
      const combos = groups.reduce((acc, g) => acc * Math.max(1, g.choices.length), 1);
      return combos <= 100;
    },
    { message: 'products.errors.optionCombinationsTooMany', path: ['optionGroups'] },
  );

export type ProductFormValues = z.infer<typeof productFormSchema>;

// 외화는 기본 null(자동계산). KRW 기준가만 필수로 입력받는다.
const zeroCurrencyPrices = (): CurrencyPrices => ({
  KRW: { listAmount: 0, saleAmount: 0 },
  USD: { listAmount: null, saleAmount: null },
  JPY: { listAmount: null, saleAmount: null },
  CNY: { listAmount: null, saleAmount: null },
});

const emptyTranslationEntry = () => ({
  name: '',
  description: '',
  brandName: '',
  eventPromoSubtitle: '',
  detailHtml: '',
  noticeText: '',
  noticeSpec: {} as Record<string, string>,
});

const emptyTranslations = (): ProductTranslations => ({
  'ko-KR': emptyTranslationEntry(),
  'en-US': emptyTranslationEntry(),
  'ja-JP': emptyTranslationEntry(),
  'zh-CN': emptyTranslationEntry(),
});

export const emptyProductFormValues = (): ProductFormValues => ({
  productType: 'GENERAL',
  status: 'PREPARING',
  displayed: true,
  b2bOnly: false,
  purchasePermission: 'ALL',
  purchaseGradeIds: [],
  benefitExclusions: [],
  benefitExclusionTarget: 'ALL',
  benefitExclusionGradeIds: [],
  mileageTarget: 'ALL',
  mileageGradeIds: [],
  mileageUseExclusionTarget: 'NONE',
  mileageUseExclusionGradeIds: [],
  purchaseDeniedText: '',
  purchaseDeniedTextEnabled: false,
  accessPermission: 'ALL',
  accessGradeIds: [],
  accessDeniedDisplay: true,
  primaryCategoryId: null,
  // mall-visibility — 기본 전체노출(전 국가몰 선택). 신규 등록 시 모든 몰에 노출.
  visibleMalls: [...PRODUCT_LOCALES],
  // QA-507 공통 동의 — 새 상품은 사용 안 함으로 시작(필요한 것만 고른다).
  commonConsentCodes: [],
  // QA-166 다중 카테고리 — 대표(primaryCategoryId) 외 추가 카테고리. 기본 빈 배열로 등록.
  additionalCategoryIds: [],
  nameKo: '',
  productCode: '',
  // QA-231 자체상품코드 기본 빈값(선택 입력).
  selfProductCode: '',
  artistId: null,
  artistMemberIds: null,
  translations: emptyTranslations(),
  listPrice: { amount: 0, currencyCode: 'KRW' },
  salePrice: { amount: 0, currencyCode: 'KRW' },
  prices: zeroCurrencyPrices(),
  // Task#4 — 옵션 선택사항: 기본은 옵션 없음(빈 배열). 저장 시 BE 가 기본 옵션을 자동 생성.
  variants: [],
  detailImages: [],
  noticeSpec: {},
  shippingFree: false,
  wishlistEnabled: true,
  shippingFeeType: 'FREE',
  shippingFeePolicyId: null,
  weightGrams: null,
  shippingPolicyId: null,
  guidePolicyId: null,
  operationPolicyId: null,
  taxExempt: false,
  searchKeywords: null,
  sortWeight: null,
  seoTitle: null,
  seoDescription: null,
  relatedProductIds: null,
  presaleStatus: null,
});
